import type { RefObject } from 'react';
import { APPLIED_FILTER_REMOVE_ATTR, useAppliedFilterRemoveFocus } from '../../hooks/useAppliedFilterRemoveFocus';
import type { FilterGroupItem, FilterItem, FilterType } from '../../utils';
import { AppliedFilter } from '../AppliedFilter';
import { IconButton } from '../Button';
import { Text } from '../Text';

export type CustomFilterPills = Array<{ id: string; label: string; onRemove: () => void; labelOnly?: boolean }>;

type AppliedFiltersRowProps = {
  filters: FilterGroupItem;
  filterTypes: FilterType[];
  handleRemoveFilter: (index: number) => void;
  customFilterPills?: CustomFilterPills;
  /** Takes focus when the last pill is removed while its remove button has focus. */
  emptyFocusRef?: RefObject<HTMLElement | null>;
};

export const AppliedFiltersRow = ({
  filters,
  filterTypes,
  handleRemoveFilter,
  customFilterPills,
  emptyFocusRef,
}: AppliedFiltersRowProps) => {
  const { rowRef, beforeRemove } = useAppliedFilterRemoveFocus(emptyFocusRef);
  const remove = (onRemove: () => void) => () => {
    beforeRemove();
    onRemove();
  };

  return (
    (filters.items.length > 0 || (typeof customFilterPills !== 'undefined' && customFilterPills.length > 0)) && (
      <div ref={rowRef} className="flex items-center gap-2 flex-wrap">
        {customFilterPills?.map((pill) =>
          pill.labelOnly ? (
            <div
              key={pill.id}
              className="flex items-center pl-2 pr-1 py-0.5 rounded-lg border bg-blue-10 border-blue-20 max-w-full overflow-hidden"
            >
              <Text className="text-xs text-blue-60 font-semibold whitespace-nowrap">{pill.label}</Text>
              <IconButton
                variant="hollow"
                icon="crossCircled"
                size="icon"
                iconSize={12}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(pill.onRemove)();
                }}
                className="rounded-full hover:opacity-70 hover:bg-transparent transition-opacity text-blue-60 cursor-pointer flex-none"
                aria-label={`Remove filter ${pill.label}`}
                {...{ [APPLIED_FILTER_REMOVE_ATTR]: true }}
                data-testid={`advanced-table-custom-filter-remove-${pill.id}`}
              />
            </div>
          ) : (
            <AppliedFilter
              key={pill.id}
              filter={{
                type: 'line',
                id: pill.id,
                operator: 'and',
                filterType: { id: pill.id, label: pill.label },
                operatorType: { id: 'eq', label: 'is' },
                value: 'visible area',
              }}
              filterTypes={filterTypes}
              onClose={remove(pill.onRemove)}
              hideColumnName={false}
            />
          ),
        )}
        {filters.items.map((filter: FilterItem, index: number) => (
          <AppliedFilter
            key={filter.id}
            filter={filter}
            filterTypes={filterTypes}
            onClose={remove(() => handleRemoveFilter(index))}
          />
        ))}
      </div>
    )
  );
};
